/**
 * Stampa del Board Giornaliero.
 *
 * Meccanismo identico a WorkOrderPrintView.tsx:
 *   buildBoardHTML → Blob → URL.createObjectURL → window.open
 * La finestra contiene una toolbar (Stampa / Chiudi) nascosta in @media print.
 *
 * Il chiamante normalizza i dati nelle due forme (live / snapshot storico)
 * nel tipo comune BoardPrintRow prima di invocare printBoard.
 */

/** Singolo intervento normalizzato per la stampa. */
export interface PrintIntervention {
  progressivo?: number
  descrizione: string
  workshop_nome?: string
  stato_nome?: string
}

/** Tipo normalizzato condiviso dalle due sorgenti del board.
 *  Campi assenti in una sorgente restano undefined. */
export interface BoardPrintRow {
  numero_scheda: string
  vehicle_targa?: string
  vehicle_marca?: string
  vehicle_modello?: string
  customer_nome?: string
  stato: string
  priorita?: string
  nota_testo?: string
  interventions?: PrintIntervention[]
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function esc(v: unknown): string {
  if (v == null || v === '') return ''
  return String(v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function escOrDash(v: unknown): string {
  const s = esc(v)
  return s || '—'
}

// ── HTML builder ──────────────────────────────────────────────────────────────

function buildBoardHTML(dateDisplay: string, righe: BoardPrintRow[]): string {
  const printedAt = new Date().toLocaleString('it-IT')
  const n = righe.length
  const nLabel = n === 1 ? '1 veicolo' : `${n} veicoli`

  const prioritaLabel: Record<string, string> = {
    urgente: 'URGENTE',
    alta: 'ALTA',
    media: 'media',
    bassa: '',
  }

  const rowsHTML = n === 0
    ? `<div class="vehicle-block" style="text-align:center;color:#999;font-style:italic;padding:20px 0">
         Nessun veicolo per questa data
       </div>`
    : righe.map(r => {
        const veicolo = [r.vehicle_marca, r.vehicle_modello].filter(Boolean).join(' ')
        const priLabel = r.priorita ? (prioritaLabel[r.priorita] ?? r.priorita.toUpperCase()) : ''
        const notaHTML = r.nota_testo
          ? `<div class="nota-text">${esc(r.nota_testo)}</div>`
          : ''

        // Interventi
        let interventionsHTML = ''
        if (r.interventions && r.interventions.length > 0) {
          const ints = r.interventions
          const conclusi = ints.filter(i => i.stato_nome?.toLowerCase() === 'concluso').length
          const header = `INTERVENTI (${conclusi}/${ints.length} CONCLUS${conclusi === 1 ? 'O' : 'I'})`
          const rows = ints.map((int, idx) => `
    <tr>
      <td class="int-num">${idx + 1}.</td>
      <td class="int-desc">${esc(int.descrizione)}</td>
      <td class="int-ws">${int.workshop_nome ? esc(int.workshop_nome) : '—'}</td>
      <td class="int-stato">${int.stato_nome ? esc(int.stato_nome) : '—'}</td>
    </tr>`).join('')
          interventionsHTML = `
<div class="interventions">
  <div class="int-header">${header}</div>
  <table class="int-table"><tbody>${rows}</tbody></table>
</div>`
        }

        return `
<div class="vehicle-block">
  <div class="vehicle-line">
    <span class="scheda">${esc(r.numero_scheda)}</span>
    <span class="sep">·</span>
    <span class="targa">${escOrDash(r.vehicle_targa)}</span>
    ${veicolo ? `<span class="veicolo">${esc(veicolo)}</span>` : ''}
    <span class="sep">·</span>
    <span class="cliente">${escOrDash(r.customer_nome)}</span>
    <span class="sep">·</span>
    <span class="stato">${esc(r.stato)}</span>
    ${priLabel ? `<span class="priorita p-${esc(r.priorita)}">${esc(priLabel)}</span>` : ''}
  </div>
  ${interventionsHTML}
  ${notaHTML}
</div>`
      }).join('')

  return `<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="utf-8">
  <title>Board Giornaliero — ${esc(dateDisplay)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; background: #f0f0f0; color: #111; }

    /* ── Toolbar (visibile solo a schermo) ── */
    #print-toolbar {
      position: fixed; top: 0; left: 0; right: 0;
      background: #1677ff; color: #fff;
      display: flex; align-items: center;
      padding: 10px 20px; gap: 12px;
      z-index: 9999; box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    }
    #print-toolbar span { font-size: 14px; font-weight: bold; flex: 1; }
    #print-toolbar button {
      padding: 6px 20px; border: none; border-radius: 4px;
      cursor: pointer; font-size: 13px; font-weight: bold;
    }
    #btn-print { background: #fff; color: #1677ff; }
    #btn-close  { background: rgba(255,255,255,0.2); color: #fff; border: 1px solid rgba(255,255,255,0.5) !important; }
    #btn-print:hover { background: #e6f7ff; }
    #btn-close:hover  { background: rgba(255,255,255,0.3); }

    /* ── Wrapper ── */
    #page-wrapper { padding: 60px 20px 20px 20px; min-height: 100vh; }
    .doc-page {
      background: #fff;
      width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      padding: 12mm 12mm 16mm 12mm;
      box-shadow: 0 4px 20px rgba(0,0,0,0.2);
    }

    @page { size: A4 portrait; margin: 10mm 12mm; }
    @media print {
      #print-toolbar { display: none !important; }
      #page-wrapper  { padding: 0; background: none; }
      body           { background: none; }
      .doc-page      { box-shadow: none; width: 100%; min-height: unset; padding: 0; margin: 0; }
    }

    /* ── Header ── */
    .doc-label { font-size: 9px; color: #777; text-transform: uppercase; letter-spacing: 0.5px; }
    .doc-title { font-size: 22px; font-weight: bold; margin: 2px 0 3px 0; }
    .doc-date  { font-size: 15px; font-weight: bold; color: #333; }
    .doc-meta  { font-size: 10px; color: #666; margin-top: 3px; }
    .divider   { border: none; border-top: 2px solid #111; margin: 8px 0 10px 0; }

    /* ── Intestazione sezione ── */
    .col-header {
      font-size: 9px; font-weight: bold; text-transform: uppercase;
      letter-spacing: 0.4px; color: #555;
      border-bottom: 1px solid #555; padding-bottom: 3px; margin-bottom: 0;
    }

    /* ── Elenco veicoli ── */
    .vehicle-block {
      break-inside: avoid; page-break-inside: avoid;
      border-bottom: 1px solid #ccc;
      /* Senza il riquadro per le note a mano i blocchi si toccherebbero:
         il respiro fra un veicolo e l'altro lo dà solo questo padding. */
      padding: 10px 0;
    }
    .vehicle-line {
      display: flex; flex-wrap: wrap; align-items: baseline;
      gap: 0 5px; font-size: 11px; margin-bottom: 4px;
    }
    .scheda  { font-weight: bold; }
    .targa   { font-weight: bold; }
    .veicolo { color: #555; }
    .cliente { }
    .stato   { font-style: italic; color: #444; text-transform: capitalize; }
    .sep     { color: #bbb; }
    .priorita { font-size: 9px; font-weight: bold; border: 1px solid #555; padding: 0 4px; border-radius: 2px; }
    .p-urgente { border-color: #000; }
    .p-alta    { border-color: #555; }
    .p-media   { border-color: #999; color: #777; }

    /* Nota del giorno */
    .nota-text {
      font-size: 10px; color: #444; font-style: italic;
      border-left: 2px solid #bbb; padding-left: 6px;
      margin-top: 4px; white-space: pre-wrap;
    }

    /* ── Elenco interventi ── */
    .interventions { margin-top: 5px; }
    .int-header {
      font-size: 9px; font-weight: bold; text-transform: uppercase;
      letter-spacing: 0.3px; color: #555; margin-bottom: 3px;
    }
    .int-table { width: 100%; border-collapse: collapse; font-size: 10px; }
    .int-table td { padding: 1px 6px 1px 0; vertical-align: top; }
    .int-num   { width: 16px; color: #888; font-size: 9px; text-align: right; padding-right: 5px; }
    .int-desc  { }
    .int-ws    { width: 52mm; color: #555; }
    .int-stato { width: 32mm; color: #555; font-style: italic; }

    /* ── Firme ── */
    .signatures {
      display: flex; gap: 40px; margin-top: 18mm; padding-top: 0;
      break-inside: avoid; page-break-inside: avoid;
    }
    .sig-box   { flex: 1; }
    .sig-line  { border-bottom: 1px solid #333; height: 28px; margin-bottom: 4px; }
    .sig-label { font-size: 10px; color: #555; text-align: center; }

    /* ── Footer ── */
    .print-footer {
      margin-top: 8px; font-size: 9px; color: #aaa;
      text-align: center; border-top: 1px solid #e8e8e8; padding-top: 4px;
    }
  </style>
</head>
<body>

<div id="print-toolbar">
  <span>Anteprima Stampa — Board Giornaliero · ${esc(dateDisplay)}</span>
  <button id="btn-print" onclick="window.print()">🖨 Stampa</button>
  <button id="btn-close"  onclick="window.close()">✕ Chiudi</button>
</div>

<div id="page-wrapper">
<div class="doc-page">

  <div>
    <div class="doc-label">Riunione di pianificazione</div>
    <div class="doc-title">Board Giornaliero</div>
    <div class="doc-date">${esc(dateDisplay)}</div>
    <div class="doc-meta">${nLabel}</div>
  </div>

  <hr class="divider">

  <div class="col-header">Scheda · Veicolo · Cliente · Stato</div>

  <div class="board-list">
    ${rowsHTML}
  </div>

  <div class="signatures">
    <div class="sig-box">
      <div class="sig-line"></div>
      <div class="sig-label">Amministrazione</div>
    </div>
    <div class="sig-box">
      <div class="sig-line"></div>
      <div class="sig-label">Capo officina</div>
    </div>
  </div>

  <div class="print-footer">
    Stampato il ${printedAt}
  </div>

</div>
</div>
</body>
</html>`
}

// ── Funzione pubblica ─────────────────────────────────────────────────────────

export function printBoard(dateDisplay: string, righe: BoardPrintRow[]): void {
  const html = buildBoardHTML(dateDisplay, righe)
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const win = window.open(url, '_blank', 'width=960,height=850')
  if (!win) {
    URL.revokeObjectURL(url)
    alert("Il browser ha bloccato l'apertura della finestra di anteprima. Abilita i popup per questo sito.")
    return
  }
  win.addEventListener('load', () => URL.revokeObjectURL(url), { once: true })
  win.focus()
}
